<script setup>
import { ref, computed } from "vue";
import { isTouchDevice } from "common/utils/browser";
import Stars from "common/vue/components/Stars.vue";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { track } from "jiffy-analytics";
import PdpMadeReview from "./PdpMadeReview.vue";

const props = defineProps({
  rating: {
    type: Number,
    required: true
  },
  reviews: {
    type: Array,
    required: true
  },
  reviewsCount: {
    type: Number,
    required: true
  }
});

const formattedReviewsCount = computed(() =>
  typeof props.reviewsCount === "number" ? props.reviewsCount.toLocaleString() : 0
);

let timerId = null;
const isTooltipOpen = ref(false);

const closeTooltip = () => {
  isTooltipOpen.value = false;
  clearTimeout(timerId);
};

const openTooltip = () => {
  if (isTouchDevice()) return;

  timerId = setTimeout(() => {
    isTooltipOpen.value = true;
    track("made_pdp_review_opened", {
      source: "Custom Apparel Reviews"
    });
  }, 500);
};

const toggleTooltip = () => {
  isTooltipOpen.value = !isTooltipOpen.value;
};

const handleReviewLinkClick = () => {
  toggleTooltip();
  track("made_see_all_reviews_clicked", {
    source: "Custom Apparel Reviews"
  });
};
</script>

<template>
  <div v-click-outside="closeTooltip" class="pdp-made-reviews" @mouseleave="closeTooltip">
    <div class="pdp-made-reviews__widget">
      <button
        class="pdp-made-reviews__rating"
        type="button"
        @click="toggleTooltip"
        @mouseenter="openTooltip"
      >
        <span class="pdp-made-reviews__rating-text hidden-xs hidden-sm">Rating:</span>
        <Stars
          class="js-feedback-label-tracker pdp-made-reviews__stars"
          :stars="rating"
          size="S"
        ></Stars>
        <span class="pdp-made-reviews__rating-text">{{ formattedReviewsCount }}</span>
        <UseSvg
          class="pdp-made-reviews__tooltip-trigger-icon"
          :class="{ 'pdp-made-reviews__tooltip-trigger-icon--flip': isTooltipOpen }"
          icon="chevron-down"
        />
      </button>
    </div>

    <div class="pdp-made-reviews__tooltip" :class="{ hidden: !isTooltipOpen }">
      <div class="pdp-made-reviews__tooltip-inner">
        <header class="pdp-made-reviews__tooltip-header">
          <h4 class="pdp-made-reviews__tooltip-header-title">Latest Reviews</h4>
          <a
            class="pdp-made-reviews__tooltip-header-link"
            data-testid="pdp-made-reviews-see-all-link"
            href="#reviews-summary"
            @click="handleReviewLinkClick"
          >
            See all customer reviews
          </a>
        </header>

        <div class="pdp-made-reviews__tooltip-body">
          <div class="pdp-made-reviews__wrapper">
            <PdpMadeReview
              v-for="review in props.reviews"
              :key="review"
              :review="review"
              :toggle-tooltip="toggleTooltip"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";
@import "frontend/init/jiffy/button/mixin";

.pdp-made-reviews {
  position: relative;

  &__widget {
    display: flex;
    gap: 12px;
  }

  &__rating {
    @include clear-button;

    display: flex;
    align-items: center;
    gap: 4px;
    text-decoration: none;
  }

  &__rating-text {
    color: $blue;
    font-size: 12px;
    font-weight: 700;

    @include with-desktop {
      font-size: 16px;
    }
  }

  &__tooltip-trigger-icon {
    width: 11px;
    height: 11px;
    margin-left: 1px;
    color: $silver-chalice;
    vertical-align: -3px;

    &--flip {
      transform: rotate(180deg);
    }
  }

  &__tooltip {
    position: absolute;
    top: 100%;
    left: 0;
    max-width: calc(100vw - 10px);
    margin-left: -3px;
    padding-top: 8px;
    z-index: 3;

    @include with-tablet {
      margin-left: 7px;
    }
  }

  &__tooltip-inner {
    border: 1px solid $blue;
    border-radius: 8px;
    background-color: $white;
    box-shadow: 0px 10px 20px rgba($black, 0.25);
    overflow: hidden;
  }

  &__tooltip-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 40px;
    padding: 0 14px 0 10px;
    border-radius: 6px 6px 0 0;
    background-color: $jiffy-sample-cta-blue;
    color: $white;
    font-size: 14px;
  }

  &__tooltip-header-title {
    font-size: inherit;
  }

  &__tooltip-header-link {
    color: inherit;
    font-size: 14px;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  &__tooltip-body {
    max-height: 310px;
    padding: 10px 8px;
    border-radius: 0 0 6px 6px;
    overflow: auto;
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
}
</style>
